/**
 * The manual review queue — every order waiting on a human.
 *
 * Four decisions, and they are not equivalent. `approve` clears the hold and lets the worker
 * resume exactly as it would have; `refund` and `reject` send the money back. **`reprice` is
 * the only one that changes what the customer is paid**, so it is the only one that asks for
 * confirmation, and the confirmation states the figures rather than asking "are you sure".
 *
 * `reprice` is also offered only where it means something. It applies the amount that actually
 * arrived, and only a review raised over an amount — an overpayment, an underpayment, a price
 * that moved past tolerance — has one recorded. A review raised because the order was over the
 * manual ceiling, or because the float was short, has nothing to re-price against; the API
 * refuses those, so the button is not shown rather than offered and then rejected.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import type { AdminTreasuryOrder, ReviewDecision, ReviewReason } from '../contracts/types.js';
import { Badge, Button, Card, EmptyState, ErrorBox, Input, LoadingState } from '../components/ui.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { formatMoney } from '../lib/money.js';
import { relativeTime } from '../lib/format.js';

/** Why the order stopped, in the operator's words rather than the enum's. */
const REASON_COPY: Record<ReviewReason, string> = {
  overpaid: 'More arrived than was quoted.',
  underpaid_expired: 'Less arrived than was quoted, and the top-up window has closed.',
  above_review_ceiling: 'Larger than the manual review ceiling.',
  liquidity_short: 'Not enough free stock to cover it when it was due to settle.',
  payout_failed: 'The payout was attempted and the rail refused it.',
  reprice_out_of_tolerance: 'The price moved further than the automatic tolerance allows.',
};

/**
 * The reasons that carry a received amount, and therefore the only ones a re-price can act on.
 *
 * Kept as a set rather than inferred from the copy above, because the distinction is a
 * behavioural one the API enforces — `receivedForReview` throws for anything else — and a
 * screen that offers a button the server will refuse is worse than one that omits it.
 */
const REPRICEABLE: ReadonlySet<string> = new Set<string>([
  'overpaid',
  'underpaid_expired',
  'reprice_out_of_tolerance',
]);

/**
 * The contract types `reviewReason` as a bounded string rather than the enum, so a reason this
 * console has never heard of is a shape it must survive rather than crash on. An unknown value
 * still renders — as the raw reason — instead of blanking the card that tells an operator an
 * order is stuck.
 */
function isKnownReason(value: string | null): value is ReviewReason {
  return value !== null && Object.prototype.hasOwnProperty.call(REASON_COPY, value);
}

export function TreasuryReviewPage(): JSX.Element {
  const { api, session } = useAuth();
  const toast = useToast();
  const canDecide = session !== null && adminCan(session.me.role, 'rates:write');
  const state = useAsync(() => api.treasuryReviewQueue(), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  async function decide(order: AdminTreasuryOrder, decision: ReviewDecision): Promise<void> {
    const note = notes[order.id] ?? '';

    if (decision === 'reprice' && !window.confirm(repriceConfirm(order))) return;
    if (
      (decision === 'refund' || decision === 'reject') &&
      !window.confirm(
        `${decision === 'refund' ? 'Refund' : 'Reject'} ${order.reference}?\n\nThe customer gets their money back and the order ends. This cannot be undone from here.`,
      )
    ) {
      return;
    }

    setBusy(order.id);
    try {
      await api.decideTreasuryReview(order.id, { decision, note });
      toast.push('success', `${order.reference}: ${decision}d.`);
      state.reload();
    } catch (error) {
      toast.push('error', 'That decision did not go through.', messageOf(error));
    } finally {
      setBusy(null);
    }
  }

  if (state.loading) return <LoadingState label="Reading the queue" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;

  const orders = state.data?.orders ?? [];

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Review queue</h1>
        <p className="page-head__sub">
          Orders the machine stopped and handed to a person. Nothing here is moving until it is
          decided.
        </p>
      </div>

      {orders.length === 0 ? (
        <Card>
          <EmptyState
            glyph="✓"
            title="Nothing is waiting"
            hint="Orders appear here when the machine will not decide them on its own."
          />
        </Card>
      ) : (
        orders.map((order) => {
          const reason = order.reviewReason;
          const repriceable = reason !== null && REPRICEABLE.has(reason);
          return (
            <Card key={order.id} title={
              <>
                <Link to={`/treasury/orders/${order.id}`}>{order.reference}</Link>{' '}
                <Badge tone={order.side === 'buy' ? 'blue' : 'violet'}>{order.side}</Badge>{' '}
                {reason !== null && <Badge tone="amber">{reason.replace(/_/g, ' ')}</Badge>}
              </>
            }>
              <p style={{ marginTop: 0 }}>{isKnownReason(reason) ? REASON_COPY[reason] : reason ?? ''}</p>

              <div className="table-wrap">
                <table className="table">
                  <tbody>
                    <tr>
                      <th>Customer</th>
                      <td>
                        {order.customer.displayName}{' '}
                        <span className="muted">{order.customer.reference}</span>
                      </td>
                    </tr>
                    <tr>
                      <th>Naira</th>
                      <td className="num">{formatMoney(order.ngnAmount)}</td>
                    </tr>
                    <tr>
                      <th>Coin</th>
                      <td className="num">{formatMoney(order.coinAmount)}</td>
                    </tr>
                    <tr>
                      <th>Opened</th>
                      <td>{relativeTime(order.createdAt)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {canDecide ? (
                <>
                  <Input
                    placeholder="Note for the audit trail (optional)"
                    value={notes[order.id] ?? ''}
                    onChange={(e) =>
                      setNotes((prev) => ({ ...prev, [order.id]: e.target.value }))
                    }
                  />
                  <div className="row-actions" style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button
                      onClick={() => void decide(order, 'approve')}
                      disabled={busy === order.id}
                    >
                      Approve
                    </Button>
                    {/* Only where an amount was recorded — see REPRICEABLE. */}
                    {repriceable && (
                      <Button
                        variant="default"
                        onClick={() => void decide(order, 'reprice')}
                        disabled={busy === order.id}
                      >
                        Re-price at what arrived
                      </Button>
                    )}
                    <Button
                      variant="default"
                      onClick={() => void decide(order, 'refund')}
                      disabled={busy === order.id}
                    >
                      Refund
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => void decide(order, 'reject')}
                      disabled={busy === order.id}
                    >
                      Reject
                    </Button>
                  </div>
                </>
              ) : (
                <p className="muted">
                  Deciding an order needs the finance grant. You can read the queue.
                </p>
              )}
            </Card>
          );
        })
      )}
    </>
  );
}

/**
 * Said before the decision, because afterwards the customer has already been paid.
 *
 * Names the two figures rather than asking "are you sure": the whole point of the decision is
 * that the amount which arrived is not the amount that was quoted, and an operator confirming
 * without seeing both numbers is confirming nothing.
 */
function repriceConfirm(order: AdminTreasuryOrder): string {
  const quoted = order.side === 'buy' ? order.coinAmount : order.ngnAmount;
  return [
    `Re-price ${order.reference} at the amount that actually arrived?`,
    '',
    `Quoted: ${formatMoney(quoted)}`,
    '',
    'The customer will be paid at the current price for what they actually sent, not at the price they were quoted. This is the only decision on this screen that changes what they receive.',
  ].join('\n');
}
